import React, { useState, useEffect } from 'react';
import { Modal, Typography, Space, Button, message, Input, DatePicker, Spin, Alert, Tag, Card } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Route } from '../../types';
import { useCreateLayoutMutation } from '../../store/api/calculationsApi';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import {
  useGetRouteViewQuery,
  useLoadRouteStopMutation,
  useUnloadRouteStopMutation
} from '../../store/api/routesApi';
import RouteEventsModal from './RouteEventsModal';
import { StatusCell } from '../../ui';
import OperationBlock from './routeDetails/OperationBlock';
import ExecutionBlock from './routeDetails/ExecutionBlock';
import FinanceBlock from './routeDetails/FinanceBlock';
import PenaltiesBlock from './routeDetails/PenaltiesBlock';
import RouteStructureBlock from './routeDetails/RouteStructureBlock';
import DocumentsBlock from './routeDetails/DocumentsBlock';
import CargoBlock from './routeDetails/CargoBlock';
import { routeViewCargoAllocationRefreshToken } from '../../utils/routeViewCargoAllocationRefreshToken';
import { useRouteCollaboration } from '../../collab/useRouteCollaboration';

const { Title, Text } = Typography;

interface RouteDetailsModalProps {
  visible: boolean;
  route: Route | null;
  onClose: () => void;
  onRefetch?: () => void;
}

const RouteDetailsModal: React.FC<RouteDetailsModalProps> = ({
  visible,
  route,
  onClose,
  onRefetch
}) => {
  const routeId = route?.id ?? 0;
  const collab = useRouteCollaboration(route?.id);
  const shouldFetch = Boolean(visible && route && routeId > 0);

  const { data: view, isLoading, isError, error, refetch } = useGetRouteViewQuery(routeId, {
    skip: !shouldFetch
  });

  const [createLayout] = useCreateLayoutMutation();
  const [loadStop, { isLoading: loadingStop }] = useLoadRouteStopMutation();
  const [unloadStop, { isLoading: unloadingStop }] = useUnloadRouteStopMutation();

  const [isEventsModalVisible, setIsEventsModalVisible] = useState(false);

  const [reopenModalVisible, setReopenModalVisible] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenLoading, setReopenLoading] = useState(false);

  const [stopAction, setStopAction] = useState<{
    visible: boolean;
    type: 'load' | 'unload' | null;
    stopId?: number;
    date?: dayjs.Dayjs;
  }>({ visible: false, type: null });

  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
    if (!token) return undefined;
    fetch(`${API_BASE_URL}/auth/profile`, {
      headers: { Authorization: token ? `Bearer ${token}` : '' }
    })
      .then((r) => r.json())
      .then((json) => {
        if (!mounted) return;
        const roleCandidate = json?.data?.role || json?.role || json?.data?.user?.role;
        const roleName = typeof roleCandidate === 'string' ? roleCandidate : roleCandidate?.name;
        setIsAdmin(roleName === 'admin');
      })
      .catch(() => {
        if (!mounted) return;
        setIsAdmin(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleReopen = async () => {
    if (!route) return;
    if (!reopenReason || reopenReason.trim().length < 5) {
      message.error('Укажите причину отката (не менее 5 символов)');
      return;
    }
    setReopenLoading(true);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
      const res = await fetch(`${API_BASE_URL}/routes/${route.id}/reopen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ reason: reopenReason })
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json?.error || 'Ошибка при откате маршрута');
      }
      message.success('Маршрут открыт для редактирования');
      setReopenModalVisible(false);
      setReopenReason('');
      window.location.reload();
    } catch (e: unknown) {
      console.error('Reopen error', e);
      message.error(e instanceof Error ? e.message : 'Ошибка при откате маршрута');
    } finally {
      setReopenLoading(false);
    }
  };

  const refreshCard = () => {
    void refetch();
    onRefetch?.();
  };

  if (!visible || !route) {
    return null;
  }

  const op = view?.operation;
  const status = op?.status ?? route.status;

  const adminReopen =
    isAdmin && status === 'completed' ? (
      <Button danger size="small" onClick={() => setReopenModalVisible(true)}>
        Откат маршрута
      </Button>
    ) : null;

  const title = (
    <Space wrap>
      <Title level={4} style={{ margin: 0 }}>
        Детали маршрута #{route.id}
      </Title>
      <StatusCell status={String(status ?? '')} />
      {op?.isCompanyRoute && <Tag color="green">Свой</Tag>}
      {String(op?.executor ?? route.executor ?? '').toUpperCase() === 'PARTNER' && (
        <Tag color="blue">Оплата партнёром</Tag>
      )}
      <Button
        size="small"
        onClick={async () => {
          try {
            const draft = await createLayout({ routeId: route.id, inputPayload: { source: 'route' } }).unwrap();
            window.location.href = `/calculations/${(draft as { id: number }).id}`;
          } catch {
            /* empty */
          }
        }}
      >
        Расчёт размещения
      </Button>
      <Button size="small" icon={<HistoryOutlined />} onClick={() => setIsEventsModalVisible(true)}>
        История событий
      </Button>
    </Space>
  );

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnHidden
      style={{ padding: 0 }}
      styles={{ body: { padding: '24px', backgroundColor: '#fff' } }}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#fff' }}>
        {isLoading && !view && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        )}

        {isError && (
          <Alert
            type="error"
            showIcon
            message="Не удалось загрузить карточку маршрута"
            description={error && 'data' in error ? String((error as { data?: unknown }).data) : undefined}
            style={{ marginBottom: 16 }}
          />
        )}

        {view && (
          <>
            {collab.softWarningsTop.map((w) => (
              <Alert key={w} type="warning" showIcon style={{ marginBottom: 10 }} message={w} />
            ))}
            {collab.staleRemoteBanner && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 10 }}
                message="Маршрут изменился удалённо"
                description="Пока вы редактируете карточку, другой пользователь внёс изменения. Рекомендуется обновить данные перед сохранением."
                action={<Button size="small" onClick={() => { collab.clearStaleBanner(); void refetch(); }}>Обновить</Button>}
              />
            )}
            {collab.conflictExplanation && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 10 }}
                message={collab.conflictExplanation.title}
                description={collab.conflictExplanation.explanation}
                action={
                  <Space>
                    <Button size="small" onClick={() => { collab.clearConflict(); void refetch(); }}>
                      Обновить данные
                    </Button>
                  </Space>
                }
              />
            )}
            <OperationBlock
              model={view.operation}
              parties={view.parties}
              applications={view.applications}
              stops={view.structure.stops}
              adminReopenButton={adminReopen}
            />
            <RouteStructureBlock
              model={view.structure}
              routeDirectionLabel={view.operation.direction.label}
              routeLoadingCity={view.operation.direction.loadingCity}
              routeUnloadingCity={view.operation.direction.unloadingCity}
              applications={view.applications}
              loadingWarehouse={view.operation.loadingWarehouse}
              unloadingWarehouse={view.operation.unloadingWarehouse}
              routeStatus={view.operation.status}
              loadingStop={loadingStop}
              unloadingStop={unloadingStop}
              onRequestLoad={(stopId) => setStopAction({ visible: true, type: 'load', stopId })}
              onRequestUnload={(stopId) => setStopAction({ visible: true, type: 'unload', stopId })}
            />
            <ExecutionBlock model={view.execution} />
            <FinanceBlock
              model={view.carrierFinance}
              payable={view.finance?.payable ?? null}
              staleWarning={collab.financeStaleHint}
            />
            <PenaltiesBlock penalties={view.routePenalties} />
            <DocumentsBlock model={view.documents} route={route} onUploadSuccess={refreshCard} />
            <CargoBlock
              cargoLines={view.cargo}
              summary={view.structure.cargoSummary}
              allocations={view.structure.allocations}
              routeId={route.id}
              linkedApplicationIds={view.operation.linkedApplicationsSummary.map((s) => s.applicationId)}
              allocationRefreshToken={routeViewCargoAllocationRefreshToken(view)}
            />
            {collab.timelineEntries.length > 0 && (
              <Card size="small" title="Коллаборативная лента изменений" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {collab.timelineEntries.slice(0, 8).map((entry) => (
                    <div key={entry.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <Text>{entry.summary}</Text>
                      <Text type="secondary">{dayjs(entry.timestamp).format('DD.MM HH:mm')}</Text>
                    </div>
                  ))}
                </Space>
              </Card>
            )}
          </>
        )}
      </div>

      <RouteEventsModal
        visible={isEventsModalVisible}
        routeId={route.id}
        onClose={() => setIsEventsModalVisible(false)}
        title={`История событий маршрута #${route.number}`}
      />

      <Modal
        title="Причина отката маршрута"
        open={reopenModalVisible}
        onCancel={() => setReopenModalVisible(false)}
        onOk={handleReopen}
        okText="Откатить"
        confirmLoading={reopenLoading}
      >
        <Input.TextArea
          rows={4}
          value={reopenReason}
          onChange={(e) => setReopenReason(e.target.value)}
          placeholder="Опишите причину отката (не менее 5 символов)"
        />
      </Modal>

      <Modal
        title={stopAction.type === 'load' ? 'Дата фактической погрузки' : 'Дата фактической выгрузки'}
        open={stopAction.visible}
        onCancel={() => setStopAction({ visible: false, type: null })}
        okText="Подтвердить"
        onOk={async () => {
          if (!stopAction.stopId) return;
          if (!stopAction.date) {
            message.error('Выберите дату');
            return;
          }
          try {
            const payload = {
              routeId: route.id,
              stopId: stopAction.stopId,
              actualAt: stopAction.date.toISOString()
            };
            if (stopAction.type === 'load') {
              await loadStop(payload).unwrap();
              message.success('Погрузка выполнена');
            } else if (stopAction.type === 'unload') {
              await unloadStop(payload).unwrap();
              message.success('Выгрузка выполнена');
            }
            setStopAction({ visible: false, type: null });
            refreshCard();
          } catch (e: unknown) {
            const err = e as { data?: { error?: string } };
            message.error(err?.data?.error || 'Ошибка выполнения операции');
          }
        }}
      >
        <DatePicker
          style={{ width: '100%' }}
          format="DD.MM.YYYY"
          value={stopAction.date}
          onChange={(d) => setStopAction((prev) => ({ ...prev, date: d || undefined }))}
        />
      </Modal>
    </Modal>
  );
};

export default RouteDetailsModal;
